import { useState, useMemo, useCallback, useEffect } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { MUNICIPIO_FORMACAO_ID } from '@/lib/municipios';

interface Requisito {
  id: string;
  carreira: string;
  meta_horas_anuais: number;
  meta_acoes_anuais: number | null;
  descricao: string | null;
  ativo: boolean;
}

interface RequisitoForm {
  carreira: string;
  meta_horas_anuais: number;
  meta_acoes_anuais: string;
  descricao: string;
}

const CARREIRAS_DEFAULT = [
  'Técnica Superior',
  'Assistente Técnico',
  'Assistente Operacional',
  'Dirigente',
  'Informática',
  'Jurídica',
];

const ANO_ATUAL = new Date().getFullYear();

function StarRating({ value, max = 5 }: { value: number; max?: number }) {
  const pct = Math.round((value / max) * 100);
  const color = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: max }).map((_, i) => (
        <div key={i} className="w-3 h-3 flex items-center justify-center">
          <i className={`${i < Math.round(value) ? 'ri-star-fill' : 'ri-star-line'} text-xs`} style={{ color: i < Math.round(value) ? color : '#d4d4d8' }} />
        </div>
      ))}
      <span className="text-[11px] font-bold ml-1" style={{ color }}>{value.toFixed(1)}</span>
    </div>
  );
}

function ProgressBar({ value, max, color = '#10b981', height = 'h-2' }: { value: number; max: number; color?: string; height?: string }) {
  const pct = max === 0 ? 0 : Math.min(Math.round((value / max) * 100), 100);
  const barColor = pct >= 100 ? '#10b981' : pct >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className={`${height} rounded-full bg-zinc-100 overflow-hidden w-full`}>
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color === 'auto' ? barColor : color }} />
    </div>
  );
}

export default function RequisitosFormacaoPage() {
  const { formacoes, participantes } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();
  const navigate = useNavigate();

  const [requisitos, setRequisitos] = useState<Requisito[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [anoSel, setAnoSel] = useState(ANO_ATUAL);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Requisito | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState<RequisitoForm>({
    carreira: CARREIRAS_DEFAULT[0],
    meta_horas_anuais: 35,
    meta_acoes_anuais: '',
    descricao: '',
  });

  const loadRequisitos = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('formacao_requisitos')
      .select('*')
      .eq('municipio_id', MUNICIPIO_FORMACAO_ID)
      .order('carreira');
    setRequisitos((data as Requisito[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { loadRequisitos(); }, [loadRequisitos]);

  // ── Calcular cumprimento por carreira ──────────────────────────────────────
  const cumprimentoPorCarreira = useMemo(() => {
    return requisitos.map((req) => {
      const funcsCarreira = funcionarios.filter((f) => f.carreira === req.carreira);
      const resultados = funcsCarreira.map((func) => {
        const partsFunc = participantes.filter((p) => p.funcionario_id === func.id);
        const horasAno = partsFunc.reduce((s, p) => {
          const form = formacoes.find((f) => f.id === p.formacao_id);
          if (!form?.data_inicio) return s;
          const ano = new Date(form.data_inicio).getFullYear();
          if (ano !== anoSel) return s;
          return s + (form.duracao_horas ?? 0);
        }, 0);
        const acoesAno = new Set(
          partsFunc
            .filter((p) => {
              const form = formacoes.find((f) => f.id === p.formacao_id);
              return form?.data_inicio && new Date(form.data_inicio).getFullYear() === anoSel;
            })
            .map((p) => p.formacao_id)
        ).size;
        const cumpre = horasAno >= req.meta_horas_anuais &&
          (req.meta_acoes_anuais === null || acoesAno >= req.meta_acoes_anuais);
        return { func, horasAno, acoesAno, cumpre };
      });

      const totalFuncs = funcsCarreira.length;
      const cumprem = resultados.filter((r) => r.cumpre).length;
      const naoCumprem = totalFuncs - cumprem;
      const taxaCumprimento = totalFuncs > 0 ? Math.round((cumprem / totalFuncs) * 100) : 0;
      const mediaHoras = totalFuncs > 0
        ? Math.round(resultados.reduce((s, r) => s + r.horasAno, 0) / totalFuncs)
        : 0;

      return { req, totalFuncs, cumprem, naoCumprem, taxaCumprimento, mediaHoras, resultados };
    });
  }, [requisitos, funcionarios, participantes, formacoes, anoSel]);

  // ── KPIs globais ──────────────────────────────────────────────────────────
  const kpisGlobais = useMemo(() => {
    const totalFuncs = funcionarios.length;
    const totalCumprem = cumprimentoPorCarreira.reduce((s, c) => s + c.cumprem, 0);
    const taxaGlobal = totalFuncs > 0 ? Math.round((totalCumprem / totalFuncs) * 100) : 0;
    const carreirasEmRisco = cumprimentoPorCarreira.filter((c) => c.taxaCumprimento < 50).length;
    return { totalFuncs, totalCumprem, taxaGlobal, carreirasEmRisco };
  }, [cumprimentoPorCarreira, funcionarios]);

  function openCreate() {
    setEditTarget(null);
    setForm({ carreira: CARREIRAS_DEFAULT[0], meta_horas_anuais: 35, meta_acoes_anuais: '', descricao: '' });
    setShowModal(true);
  }

  function openEdit(req: Requisito) {
    setEditTarget(req);
    setForm({
      carreira: req.carreira,
      meta_horas_anuais: req.meta_horas_anuais,
      meta_acoes_anuais: req.meta_acoes_anuais?.toString() ?? '',
      descricao: req.descricao ?? '',
    });
    setShowModal(true);
  }

  async function handleSave() {
    setSaving(true);
    const payload = {
      municipio_id: MUNICIPIO_FORMACAO_ID,
      carreira: form.carreira,
      meta_horas_anuais: form.meta_horas_anuais,
      meta_acoes_anuais: form.meta_acoes_anuais ? parseInt(form.meta_acoes_anuais) : null,
      descricao: form.descricao || null,
      updated_at: new Date().toISOString(),
    };
    if (editTarget) {
      await supabase.from('formacao_requisitos').update(payload).eq('id', editTarget.id);
    } else {
      await supabase.from('formacao_requisitos').insert(payload);
    }
    await loadRequisitos();
    setShowModal(false);
    setSaving(false);
    setSuccessMsg(editTarget ? 'Requisito atualizado!' : 'Requisito criado!');
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  async function handleDelete(id: string) {
    await supabase.from('formacao_requisitos').delete().eq('id', id);
    setRequisitos((prev) => prev.filter((r) => r.id !== id));
    setConfirmDelete(null);
  }

  const anos = useMemo(() => {
    const set = new Set<number>();
    formacoes.forEach((f) => { if (f.data_inicio) set.add(new Date(f.data_inicio).getFullYear()); });
    set.add(ANO_ATUAL);
    return Array.from(set).sort((a, b) => b - a);
  }, [formacoes]);

  // ── Painel LNF: necessidades sem formação associada ──────────────────────────
  const necessidadesSemFormacao = useMemo(() => {
    return requisitos.map((req) => {
      // Formações do ano selecionado que cobrem esta carreira (por categoria ou nome)
      const formacoesCarreira = formacoes.filter((f) => {
        if (!f.data_inicio) return false;
        const ano = new Date(f.data_inicio).getFullYear();
        if (ano !== anoSel) return false;
        if (f.estado === 'Cancelada') return false;
        // Verifica se a formação tem participantes desta carreira
        const partsFormacao = participantes.filter((p) => p.formacao_id === f.id);
        const temFuncCarreira = partsFormacao.some((p) => {
          const func = funcionarios.find((fn) => fn.id === p.funcionario_id);
          return func?.carreira === req.carreira;
        });
        return temFuncCarreira;
      });

      const funcsCarreira = funcionarios.filter((f) => f.carreira === req.carreira);
      const semQualquerFormacao = funcsCarreira.filter((func) => {
        const partsFunc = participantes.filter((p) => p.funcionario_id === func.id);
        const temFormacaoAno = partsFunc.some((p) => {
          const form = formacoes.find((f) => f.id === p.formacao_id);
          return form?.data_inicio && new Date(form.data_inicio).getFullYear() === anoSel && form.estado !== 'Cancelada';
        });
        return !temFormacaoAno;
      });

      return {
        req,
        formacoesAssociadas: formacoesCarreira.length,
        totalFuncs: funcsCarreira.length,
        semFormacao: semQualquerFormacao.length,
        funcionariosSemFormacao: semQualquerFormacao,
        temNecessidade: semQualquerFormacao.length > 0,
      };
    }).filter((n) => n.totalFuncs > 0);
  }, [requisitos, formacoes, participantes, funcionarios, anoSel]);

  const totalSemFormacao = necessidadesSemFormacao.reduce((s, n) => s + n.semFormacao, 0);
  const carreirasSemFormacao = necessidadesSemFormacao.filter((n) => n.temNecessidade).length;

  return (
    <>
      <main className="flex-1 bg-slate-50/60 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-scales-3-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Requisitos Legais</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Metas de horas de formação por carreira — PDR / 35h anuais</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Seletor de ano */}
              <div className="flex items-center bg-zinc-100 rounded-lg p-0.5 gap-0.5">
                {anos.slice(0, 4).map((a) => (
                  <button
                    key={a}
                    onClick={() => setAnoSel(a)}
                    className={`whitespace-nowrap px-3.5 py-1.5 rounded-md text-[12px] font-semibold transition-all cursor-pointer ${anoSel === a ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
              <button
                onClick={openCreate}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                Nova Meta
              </button>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">
          {/* Banner sucesso */}
          {successMsg && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-4 py-3 rounded-xl">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-sm" /></div>
              {successMsg}
            </div>
          )}

          {/* KPIs globais */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: 'ri-scales-3-line', value: requisitos.length, label: 'Metas Configuradas', bg: 'bg-zinc-100 text-zinc-600' },
              { icon: 'ri-group-line', value: `${kpisGlobais.totalCumprem}/${kpisGlobais.totalFuncs}`, label: 'Cumprem a Meta', bg: 'bg-emerald-50 text-emerald-600' },
              { icon: 'ri-percent-line', value: `${kpisGlobais.taxaGlobal}%`, label: 'Taxa de Cumprimento', bg: kpisGlobais.taxaGlobal >= 70 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600' },
              { icon: 'ri-alert-line', value: kpisGlobais.carreirasEmRisco, label: 'Carreiras em Risco', bg: kpisGlobais.carreirasEmRisco > 0 ? 'bg-rose-50 text-rose-600' : 'bg-zinc-100 text-zinc-600' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                  <i className={`${s.icon} text-base`} />
                </div>
                <div>
                  <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                  <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Aviso legal */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-600 text-sm" />
            </div>
            <div>
              <p className="text-[13px] font-bold text-amber-800">Requisito Legal — PDR e LTFP</p>
              <p className="text-[12px] text-amber-700 mt-0.5">
                A Lei Geral do Trabalho em Funções Públicas (LTFP) e o Plano de Desenvolvimento de Recursos Humanos (PDR) estabelecem
                um mínimo de <strong>35 horas anuais</strong> de formação para trabalhadores em funções públicas.
                Configure as metas por carreira e acompanhe o cumprimento em tempo real.
              </p>
            </div>
          </div>

          {/* Painel LNF — Necessidades sem formação associada */}
          {!loading && requisitos.length > 0 && (
            <div className={`rounded-xl border overflow-hidden ${
              carreirasSemFormacao > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
            }`}>
              <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                    carreirasSemFormacao > 0 ? 'bg-amber-100' : 'bg-emerald-100'
                  }`}>
                    <i className={`text-base ${
                      carreirasSemFormacao > 0 ? 'ri-alert-line text-amber-600' : 'ri-checkbox-circle-line text-emerald-600'
                    }`} />
                  </div>
                  <div>
                    <p className={`text-[14px] font-bold ${
                      carreirasSemFormacao > 0 ? 'text-amber-900' : 'text-emerald-900'
                    }`}>
                      {carreirasSemFormacao > 0
                        ? `${carreirasSemFormacao} carreira(s) com necessidades LNF por agendar em ${anoSel}`
                        : `Todas as carreiras têm formação associada em ${anoSel}`
                      }
                    </p>
                    <p className={`text-[12px] mt-0.5 ${
                      carreirasSemFormacao > 0 ? 'text-amber-700' : 'text-emerald-700'
                    }`}>
                      {carreirasSemFormacao > 0
                        ? `${totalSemFormacao} funcionário(s) ainda não têm formação planeada este ano`
                        : 'Todas as necessidades de formação estão cobertas'
                      }
                    </p>
                  </div>
                </div>
                {carreirasSemFormacao > 0 && (
                  <button
                    onClick={() => navigate(`/formacao/acoes?ano=${anoSel}&nova=1`)}
                    className="whitespace-nowrap flex items-center gap-2 bg-amber-600 text-white text-[12px] font-semibold px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                    Criar formação para {anoSel}
                  </button>
                )}
              </div>

              {carreirasSemFormacao > 0 && (
                <div className="border-t border-amber-200 divide-y divide-amber-100">
                  {necessidadesSemFormacao
                    .filter((n) => n.temNecessidade)
                    .map((n) => (
                      <div key={n.req.id} className="px-6 py-3.5 flex items-center gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[13px] font-bold text-amber-900">{n.req.carreira}</span>
                            <span className="text-[10px] font-bold bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                              {n.semFormacao} sem formação
                            </span>
                            {n.formacoesAssociadas > 0 && (
                              <span className="text-[10px] font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                {n.formacoesAssociadas} formação(oes) existente(s)
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-amber-700 mt-0.5">
                            Meta: {n.req.meta_horas_anuais}h anuais
                            {n.req.meta_acoes_anuais ? ` · ${n.req.meta_acoes_anuais} ações mín.` : ''}
                          </p>
                          {/* Lista de funcionários sem formação */}
                          {n.funcionariosSemFormacao.length > 0 && (
                            <details className="mt-2">
                              <summary className="text-[11px] font-semibold text-amber-700 cursor-pointer list-none flex items-center gap-1 hover:text-amber-900">
                                <i className="ri-arrow-down-s-line text-sm" />
                                Ver {n.funcionariosSemFormacao.length} funcionário(s) sem formação
                              </summary>
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {n.funcionariosSemFormacao.slice(0, 20).map((func) => (
                                  <span
                                    key={func.id}
                                    className="text-[11px] bg-white border border-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium"
                                  >
                                    {func.nome}
                                  </span>
                                ))}
                                {n.funcionariosSemFormacao.length > 20 && (
                                  <span className="text-[11px] text-amber-600">+{n.funcionariosSemFormacao.length - 20} mais</span>
                                )}
                              </div>
                            </details>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-[18px] font-black text-amber-800">{Math.round((n.semFormacao / n.totalFuncs) * 100)}%</p>
                            <p className="text-[10px] text-amber-600">sem cobertura</p>
                          </div>
                          <button
                            onClick={() => navigate(`/formacao/acoes?ano=${anoSel}&nova=1`)}
                            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors cursor-pointer"
                            title="Criar formação para esta carreira"
                          >
                            <i className="ri-add-line text-sm" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
              </div>
              <p className="text-[13px] text-zinc-400">A carregar requisitos...</p>
            </div>
          ) : requisitos.length === 0 ? (
            <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-scales-3-line text-4xl text-zinc-200" />
              </div>
              <p className="text-[14px] font-semibold text-zinc-400">Nenhuma meta configurada</p>
              <p className="text-[12px] text-zinc-300 text-center max-w-[300px]">
                Cria metas de horas por carreira para acompanhar o cumprimento legal.
              </p>
              <button
                onClick={openCreate}
                className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
                Criar primeira meta
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cumprimentoPorCarreira.map(({ req, totalFuncs, cumprem, naoCumprem, taxaCumprimento, mediaHoras, resultados }) => {
                const statusColor = taxaCumprimento >= 80 ? 'text-emerald-600' : taxaCumprimento >= 50 ? 'text-amber-600' : 'text-rose-600';
                const statusBg = taxaCumprimento >= 80 ? 'bg-emerald-50 border-emerald-200' : taxaCumprimento >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200';
                const barColor = taxaCumprimento >= 80 ? '#10b981' : taxaCumprimento >= 50 ? '#f59e0b' : '#ef4444';

                return (
                  <div key={req.id} className={`bg-white rounded-xl border overflow-hidden ${taxaCumprimento < 50 ? 'border-rose-200' : 'border-zinc-100'}`}>
                    {/* Cabeçalho */}
                    <div className="px-6 py-4 flex items-center gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-[15px] font-bold text-zinc-900">{req.carreira}</h3>
                          {taxaCumprimento < 50 && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                              Em Risco
                            </span>
                          )}
                          {taxaCumprimento >= 80 && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                              Bom Cumprimento
                            </span>
                          )}
                        </div>
                        {req.descricao && (
                          <p className="text-[11px] text-zinc-400 mt-0.5">{req.descricao}</p>
                        )}
                      </div>

                      {/* Meta */}
                      <div className="flex items-center gap-6 flex-shrink-0">
                        <div className="text-center">
                          <p className="text-[22px] font-black text-zinc-900">{req.meta_horas_anuais}h</p>
                          <p className="text-[10px] text-zinc-400">meta anual</p>
                        </div>
                        {req.meta_acoes_anuais && (
                          <div className="text-center">
                            <p className="text-[22px] font-black text-zinc-900">{req.meta_acoes_anuais}</p>
                            <p className="text-[10px] text-zinc-400">ações mín.</p>
                          </div>
                        )}
                        <div className="text-center">
                          <p className={`text-[22px] font-black ${statusColor}`}>{taxaCumprimento}%</p>
                          <p className="text-[10px] text-zinc-400">cumprimento</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(req)}
                            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-edit-line text-sm" />
                          </button>
                          {confirmDelete === req.id ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleDelete(req.id)} className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 px-2 py-1 rounded-lg cursor-pointer">Sim</button>
                              <button onClick={() => setConfirmDelete(null)} className="whitespace-nowrap text-[11px] text-zinc-400 bg-zinc-100 px-2 py-1 rounded-lg cursor-pointer">Não</button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDelete(req.id)}
                              className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Barra de progresso */}
                    <div className="px-6 pb-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] text-zinc-500">
                          {cumprem} de {totalFuncs} funcionários cumprem a meta em {anoSel}
                        </span>
                        <span className="text-[11px] text-zinc-400">Média: {mediaHoras}h/{req.meta_horas_anuais}h</span>
                      </div>
                      <ProgressBar value={cumprem} max={totalFuncs} color={barColor} height="h-2.5" />
                    </div>

                    {/* Resumo por estado */}
                    <div className={`px-6 py-3 border-t ${statusBg} flex items-center gap-6 flex-wrap`}>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100">
                          <i className="ri-check-line text-emerald-600 text-xs" />
                        </div>
                        <span className="text-[12px] font-semibold text-emerald-700">{cumprem} cumprem</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-rose-100">
                          <i className="ri-close-line text-rose-600 text-xs" />
                        </div>
                        <span className="text-[12px] font-semibold text-rose-700">{naoCumprem} não cumprem</span>
                      </div>
                      {naoCumprem > 0 && (
                        <div className="ml-auto">
                          <details className="cursor-pointer">
                            <summary className="text-[11px] font-semibold text-zinc-500 hover:text-zinc-700 list-none flex items-center gap-1 cursor-pointer">
                              <i className="ri-arrow-down-s-line text-sm" />
                              Ver funcionários em falta
                            </summary>
                            <div className="mt-3 space-y-1.5 max-h-48 overflow-y-auto">
                              {resultados
                                .filter((r) => !r.cumpre)
                                .sort((a, b) => b.horasAno - a.horasAno)
                                .map((r) => (
                                  <div key={r.func.id} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-zinc-100">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[12px] font-semibold text-zinc-800">{r.func.nome}</p>
                                      <p className="text-[10px] text-zinc-400">{r.func.unidadeOrganica ?? '—'}</p>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                      <div className="text-right">
                                        <p className="text-[13px] font-bold text-zinc-700">{r.horasAno}h</p>
                                        <p className="text-[10px] text-zinc-400">de {req.meta_horas_anuais}h</p>
                                      </div>
                                      <div className="w-16">
                                        <ProgressBar value={r.horasAno} max={req.meta_horas_anuais} color="auto" height="h-1.5" />
                                      </div>
                                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${r.horasAno === 0 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {r.horasAno === 0 ? 'Sem formação' : `Faltam ${req.meta_horas_anuais - r.horasAno}h`}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Modal criar/editar */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-md mx-4">
            <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
                  <i className="ri-scales-3-line text-white text-base" />
                </div>
                <div>
                  <h2 className="text-[16px] font-bold text-zinc-900">{editTarget ? 'Editar Meta' : 'Nova Meta de Formação'}</h2>
                  <p className="text-[11px] text-zinc-400">Requisito legal por carreira</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-7 py-5 space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Carreira <span className="text-rose-500">*</span></label>
                <select
                  value={form.carreira}
                  onChange={(e) => setForm((p) => ({ ...p, carreira: e.target.value }))}
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
                >
                  {CARREIRAS_DEFAULT.map((c) => <option key={c} value={c}>{c}</option>)}
                  <option value="Outra">Outra</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Meta de Horas Anuais <span className="text-rose-500">*</span></label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={form.meta_horas_anuais}
                      onChange={(e) => setForm((p) => ({ ...p, meta_horas_anuais: parseInt(e.target.value) || 35 }))}
                      className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-zinc-400 pointer-events-none">h</span>
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-1">Mínimo legal: 35h</p>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Nº Mínimo de Ações <span className="text-zinc-400 font-normal">(opcional)</span></label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={form.meta_acoes_anuais}
                    onChange={(e) => setForm((p) => ({ ...p, meta_acoes_anuais: e.target.value }))}
                    placeholder="Ex: 2"
                    className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 placeholder-zinc-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Descrição / Nota Legal <span className="text-zinc-400 font-normal">(opcional)</span></label>
                <textarea
                  value={form.descricao}
                  onChange={(e) => setForm((p) => ({ ...p, descricao: e.target.value }))}
                  rows={2}
                  placeholder="Ex: Conforme art. 73.º da LTFP — mínimo 35h anuais"
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
                  maxLength={500}
                />
              </div>
              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
                <button onClick={() => setShowModal(false)} className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 cursor-pointer">
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 cursor-pointer disabled:opacity-50"
                >
                  {saving ? <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>A guardar...</> : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
